import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../AuthProvider';
import Settings, { type OwnedTitleOption } from '../components/Settings';
import GuestSettings from '../components/GuestSettings';
import ItemService from '../services/item.service';

import './profile.css';

import { IMAGES } from '../utils/images';
import { COLORS } from '../utils/colors';

type ProfileTabId = 0;
// type ProfileTabId = 0 | 1; // restore when Statistics tab returns

const ProfilePage: React.FC = () => {
    const { user } = useAuth();
    const [viewing, setViewing] = useState<ProfileTabId>(0);
    const [ownedTitles, setOwnedTitles] = useState<OwnedTitleOption[]>([]);
    const [ownedTitlesLoading, setOwnedTitlesLoading] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        if (user.guest || typeof user.userId !== 'number') {
            setOwnedTitles([]);
            setOwnedTitlesLoading(false);
            return;
        }

        let cancelled = false;
        setOwnedTitlesLoading(true);

        ItemService.getByType(user.userId, 'TITLE')
            .then(({ data }) => {
                if (cancelled) {
                    return;
                }
                setOwnedTitles(
                    data.map((item) => ({
                        id: item.id,
                        text: item.shopName,
                    }))
                );
            })
            .catch((err) => {
                console.error('Failed to load owned titles', err);
                if (!cancelled) {
                    setOwnedTitles([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setOwnedTitlesLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [user.guest, user.userId]);

    const profileTitleLabel = useMemo(() => {
        if (user.guest) {
            return 'Cadet';
        }
        const row = user.settings?.title;
        if (row?.fileName) {
            return row.fileName;
        }
        if (row?.shopName) {
            return row.shopName;
        }
        const id = user.settings?.titleId;
        if (id != null && ownedTitles.length > 0) {
            const match = ownedTitles.find((o) => o.id === id);
            if (match) {
                return match.text;
            }
        }
        return '—';
    }, [user.settings, ownedTitles]);

    const options: { id: ProfileTabId; title: string }[] = [
        { id: 0, title: 'Settings' },
        // { id: 1, title: 'Statistics' },
    ];

    function getDisplay() {
        if (user.guest) {
            return <GuestSettings />;
        }
        switch (viewing) {
            case 0:
                return (
                    <Settings
                        user={user}
                        ownedTitles={ownedTitles}
                        ownedTitlesLoading={ownedTitlesLoading}
                    />
                );
            default:
                return <div className="settings_container" />;
        }
    }

    return (
        <div
            className="profile_page_container"
            style={{ backgroundImage: `url(${IMAGES.backgrounds.room})` }}
        >
            <div className="profile_top_bar">
                <button type="button" className="button_std" onClick={() => navigate('/home')}>
                    Back
                </button>
                <img src={IMAGES.logo} alt="" className="profile_top_bar_logo" />
            </div>
            <div className="profile_main_container">
                <div className="user_info_container">
                    <div className="profile_picture">
                        <img
                            src={IMAGES.profile_pics['default.png']}
                            alt=""
                            className="profile_pic"
                        />
                    </div>
                    <div className="profile_name">{user.username}</div>
                    <div className="profile_rank">{profileTitleLabel}</div>
                </div>
                <div className="profile_outside_options_container">
                    {!user.guest && (
                        <div className="profile_display_options_container">
                            {options.map((option) => (
                                <div
                                    key={option.id}
                                    role="button"
                                    tabIndex={0}
                                    className="profile_display_option"
                                    onClick={() => setViewing(option.id)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            setViewing(option.id);
                                        }
                                    }}
                                    style={
                                        viewing === option.id
                                            ? { backgroundColor: COLORS.lobby.primary }
                                            : { backgroundColor: COLORS.lobby.secondary }
                                    }
                                >
                                    {option.title}
                                </div>
                            ))}
                        </div>
                    )}
                    {getDisplay()}
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
